// Auth module - uses httpOnly cookies for token storage (set by backend)
// Only user profile data is stored in localStorage (never the token)

// Parse a response as JSON, or report a clear error when the host answers with
// something else. A cold-starting or overloaded server sends an HTML error page,
// and calling response.json() on it throws a SyntaxError that is indistinguishable
// from a network failure by the time it reaches the UI.
const parseJsonResponse = response => {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
        console.error('Non-JSON response from', response.url, response.status, contentType);
        return { error: "The server is temporarily unavailable. Please try again in a moment." };
    }
    return response.json();
};

// The request never completed - offline, DNS failure, CORS rejection, or blocked
// by the browser. Keep the real error in the console; show the user something useful.
const reportNetworkError = err => {
    console.error('Network request failed:', err);
    return { error: "Unable to reach the server. Check your connection and try again." };
};

export const signup = user => {
    return fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(user)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const signin = user => {
    const baseURL = process.env.REACT_APP_API_URL;

    if (!baseURL) {
        throw new Error('REACT_APP_API_URL is not defined – check your .env file');
    }

    const url = `${baseURL}/zoekedSecureSignin`;

    return fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(user)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

// Store user profile data after successful signin
export const authenticate = (data, next) => {
    if (typeof window === 'undefined') return;

    // Store only user profile, NOT the token (token is in httpOnly cookie)
    if (data && data.user) {
        try {
            localStorage.setItem('user', JSON.stringify(data.user));
        } catch (err) {
            // Safari private browsing throws QuotaExceededError
            console.warn('localStorage unavailable:', err);
        }
    }

    if (typeof next === 'function') next();
};

export const setName = (name, next) => {
    if (typeof window !== 'undefined') {
        try {
            sessionStorage.setItem('username', JSON.stringify(name));
        } catch (err) {
            console.warn('sessionStorage unavailable:', err);
        }
        next();
    }
};

export const signout = next => {
    if (typeof window !== 'undefined') {
        try {
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('username');
        } catch (err) {
            console.warn('Storage cleanup failed:', err);
        }
    }
    return fetch(`${process.env.REACT_APP_API_URL}/zoekedSecureSignout`, {
        method: 'GET',
        credentials: 'include'
    })
        .then(response => {
            if (typeof next === 'function') next();
            return parseJsonResponse(response);
        })
        .catch(err => {
            if (typeof next === 'function') next();
            return reportNetworkError(err);
        });
};

// Check if user is authenticated by reading stored profile
// The actual auth token is in an httpOnly cookie (not accessible from JS)
export const isAuthenticated = () => {
    if (typeof window === 'undefined') return false;

    let raw;
    try {
        raw = localStorage.getItem('user');
    } catch (err) {
        return false;
    }
    if (!raw) return false;

    try {
        const user = JSON.parse(raw);
        if (!user || !user._id) return false;
        return {
            userId: user._id,
            name: user.name,
            lastName: user.lastName,
            email: user.email,
            companyName: user.companyName,
            companyType: user.companyType,
            role: user.role,
            mustChangePassword: user.mustChangePassword || false
        };
    } catch {
        return false;
    }
};

// Called when an authenticated API call returns 401: clear stale localStorage
// and force a full reload to /signin so any in-memory state is dropped.
// Only redirects when the user thought they were signed in — public pages that
// happen to receive a 401 won't drag an anonymous visitor to /signin.
export const handleAuthFailure = () => {
    if (typeof window === 'undefined') return;
    let hadUser = false;
    try {
        hadUser = !!localStorage.getItem('user');
        localStorage.removeItem('user');
    } catch (err) { /* ignore */ }
    if (hadUser && window.location.pathname !== '/signin') {
        window.location.href = '/signin?expired=1';
    }
};

// Standard wrapper for fetch responses on authenticated endpoints. Detects 401,
// clears stale auth, redirects to /signin, and returns a safe error object so
// callers don't crash before the redirect kicks in. Also normalizes null/
// undefined bodies (e.g. `res.json(undefined)` from the backend when an empty
// array is dereferenced) to `{}` so common `if (data.error)` guards never NPE.
export const handleAuthResponse = async (response) => {
    if (response && response.status === 401) {
        handleAuthFailure();
        return { error: "Unauthorized" };
    }
    let body;
    try {
        body = await response.json();
    } catch (err) {
        return { error: "Invalid server response" };
    }
    return body == null ? {} : body;
};

export const forgotPassword = bodyData => {
    return fetch(`${process.env.REACT_APP_API_URL}/forgot-password`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(bodyData)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const resetPassword = resetInfo => {
    return fetch(`${process.env.REACT_APP_API_URL}/reset-password/`, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(resetInfo)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const socialLogin = user => {
    return fetch(`${process.env.REACT_APP_API_URL}/social-login/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(user)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const linkedInAuth = (formData) => {
    return fetch(`${process.env.REACT_APP_API_URL}/linkedin-auth`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
        },
        credentials: 'include',
        body: formData
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const linkedInDetails = (token) => {
    return fetch(`${process.env.REACT_APP_API_URL}/linkedin-details`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`
        },
        credentials: 'include'
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const linkedInEmail = (token) => {
    return fetch(`${process.env.REACT_APP_API_URL}/linkedin-email`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`
        },
        credentials: 'include'
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const linkedInLogin = user => {
    return fetch(`${process.env.REACT_APP_API_URL}/linkedin-login/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(user)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const linkedInPic = (token) => {
    return fetch(`${process.env.REACT_APP_API_URL}/linkedIn-pic`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`
        },
        credentials: 'include'
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const changePassword = (passwords) => {
    return fetch(`${process.env.REACT_APP_API_URL}/change-password`, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(passwords)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};

export const syncUserFromServer = async () => {
    const auth = isAuthenticated();
    if (!auth) return;

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
            method: 'GET',
            credentials: 'include'
        });
        // Cookie expired or invalid — clear the stale localStorage profile and
        // bounce to /signin. Without this, PrivateRoute keeps letting the user
        // navigate gated pages even though the backend will reject every call.
        if (res.status === 401) {
            handleAuthFailure();
            return;
        }
        if (res.ok) {
            const serverUser = await res.json();
            try {
                const raw = localStorage.getItem('user');
                if (raw) {
                    const stored = JSON.parse(raw);
                    const merged = { ...stored, mustChangePassword: serverUser.mustChangePassword || false };
                    localStorage.setItem('user', JSON.stringify(merged));
                }
            } catch (err) {
                console.warn('localStorage sync failed:', err);
            }
        }
    } catch (err) {
        console.error('Failed to sync user:', err);
    }
};

// Actively verify the httpOnly session cookie is still valid by pinging the
// gated /me endpoint. On 401 (cookie expired/invalid) it clears the stale
// localStorage profile and bounces the user to /signin. Network errors are
// ignored so a momentary offline blip never logs anyone out. Throttled so
// rapid focus/route changes don't hammer the server.
let lastSessionCheck = 0;
export const verifySession = async ({ force = false } = {}) => {
    if (typeof window === 'undefined') return;
    // Nothing to verify if the user was never signed in on this client;
    // PrivateRoute already redirects those cases.
    if (!isAuthenticated()) return;

    const now = Date.now();
    if (!force && now - lastSessionCheck < 15000) return; // 15s throttle
    lastSessionCheck = now;

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
            method: 'GET',
            credentials: 'include'
        });
        if (res.status === 401) {
            handleAuthFailure();
        }
    } catch (err) {
        // Offline / network error — don't force a logout on a transient failure.
    }
};

export const signinZendesk = (body) => {
    return fetch(`${process.env.REACT_APP_API_URL}/zendesk-auth`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
    })
        .then(parseJsonResponse)
        .catch(reportNetworkError);
};
